import {Component, computed, effect, EventEmitter, inject, OnInit, Output, Signal} from '@angular/core';
import {LessonFacetsService} from '@modules/activities/core/lesson-facets/lesson-facets.service';
import {LessonFacetItem} from '@modules/activities/core/lesson-facets/models';
import {ActivityRepositoryService} from '@modules/activities/core/services/activity-repository.service';
import {DataEntity} from 'octopus-connect';
import {ConceptEntity} from 'shared/models';
import {ChapterEntity, ChapterFilters, ChaptersService} from 'fuse-core/services/chapters.service';
import {LessonGranuleEntity} from '@modules/activities/core/models';
import {CommunicationCenterService} from "@modules/communication-center";
import {toSignal} from "@angular/core/rxjs-interop";
import {
    ALLOWED_ACTIVITY_SEARCH_STEPS,
    ActivitySearchEngineConfig,
    ActivitySearchEngineStep,
    DEFAULT_ACTIVITY_SEARCH_STEPS
} from "@modules/activities/core/activities-search-engine/activity-search-engine.config";
import {EducationalLevelService} from "@modules/activities/core/services/educational-level.service";
import {take} from "rxjs/operators";
import {Filter} from "@modules/activities/core/activities-search-engine/filter-selector/filter";

// Add a dedicated interface so types are explicit and reusable
interface LessonSearchOptions {
    flattenSubLesson: boolean;
    concepts: ConceptEntity[];
    educationalLevels: LessonFacetItem[];
    chapters: ChapterEntity[];
}

@Component({
    selector: 'app-activity-search-engine-panel',
    templateUrl: './activity-search-engine-panel.component.html',
    styleUrls: ['./activity-search-engine-panel.component.scss'],
    standalone: false
})
export class ActivitySearchEnginePanelComponent implements OnInit {
    @Output() selectionValidated = new EventEmitter<(string | number)[]>();

    private chaptersService = inject(ChaptersService);
    private activityRepositoryService = inject(ActivityRepositoryService);
    private communicationCenter = inject(CommunicationCenterService);
    private educationalLevelService = inject(EducationalLevelService);
    private lessonFacetsService = inject(LessonFacetsService);

    public state: ActivitySearchEngineStep = ActivitySearchEngineStep.CONCEPTS;
    // Strongly typed and initialized with safe defaults
    public lessonSearchOptions: LessonSearchOptions = {
        flattenSubLesson: false,
        concepts: [],
        educationalLevels: [],
        chapters: []
    };
    public selectedActivityIdList: { index: string, activity: (string | number) }[] = [];

    public selectedConcepts: ConceptEntity[] = [];
    public selectedEducationalLevels: LessonFacetItem[] = [];
    public availableEducationalLevelsToFilters: Filter<LessonFacetItem>[] = [];
    public isEducationalLevelsLoading = false;

    private selectedChapters: ChapterEntity[] = [];
    // Allow null explicitly on the bypassed state
    private stateBypassedByUser: ActivitySearchEngineStep | null = null;

    // Use the explicit interface for the config Signal
    private config: Signal<ActivitySearchEngineConfig> = toSignal(
        this.communicationCenter.getRoom('activity-search-engine').getSubject<ActivitySearchEngineConfig>('config'),
        {initialValue: {} as ActivitySearchEngineConfig}
    );

    // Strongly typed computed signal (boolean)
    private flattenSubLesson: Signal<boolean> = computed<boolean>(() => this.config()?.flattenSubLesson ?? false);

    private configuredSteps: Signal<ActivitySearchEngineStep[]> = computed<ActivitySearchEngineStep[]>(() =>
        this.normalizeSteps(this.config()?.steps)
    );

    private educationalLevelsLoaded = false;

    constructor() {
        effect(() => {
            const steps = this.configuredSteps();
            this.syncSelectionsWithSteps(steps);
            this.applyConfigSelections(this.config(), steps);
            if (!steps.includes(this.state)) {
                this.setState(this.getFirstStep());
            }
            if (this.stateBypassedByUser && !steps.includes(this.stateBypassedByUser)) {
                this.stateBypassedByUser = null;
            }
            this.ensureEducationalLevelsLoaded(steps);
        });
    }

    ngOnInit(): void {
        this.setState(this.getFirstStep());
    }

    public filters(): ({ label: string, onClick: ($event: MouseEvent) => void })[] {
        return [
            ...this.createFilterItems(this.selectedConcepts, this.onActiveConceptFilterClick.bind(this)),
            ...this.createFilterItems(this.selectedEducationalLevels, this.onActiveEducationalLevelFilterClick.bind(this)),
            ...this.createFilterItems(this.selectedChapters, this.onActiveChapterFilterClick.bind(this)),
        ];
    }

    private createFilterItems<T extends DataEntity | LessonFacetItem, U extends T[]>(items: U, onClick: ($event: MouseEvent, item: T) => void) {
        // TODO pas super propre désolé
        const isDataEntity = (item: DataEntity | LessonFacetItem): item is DataEntity => 'attributes' in item;
        const getLabel = (item: T): string => isDataEntity(item)
            ? (item.get('name') ?? item.get('label'))
            : item.name;

        return items.map(item => ({
            label: getLabel(item),
            onClick: ($event: MouseEvent) => onClick($event, item)
        }));
    }

    public clearFilters(): void {
        this.selectedConcepts = [];
        this.selectedEducationalLevels = [];
        this.selectedChapters = [];
        this.setState(this.getFirstStep());
    }

    private getLessonsSearchOptions(): LessonSearchOptions {
        const steps = this.configuredSteps();
        const hasEducationalLevels = this.hasEducationalLevelsStep(steps);
        return {
            flattenSubLesson: this.flattenSubLesson(),
            concepts: steps.includes(ActivitySearchEngineStep.CONCEPTS) ? this.selectedConcepts.slice() : [],
            educationalLevels: hasEducationalLevels ? this.selectedEducationalLevels.slice() : [],
            chapters: steps.includes(ActivitySearchEngineStep.CHAPTERS) ? this.selectedChapters.slice() : [],
        };
    }

    public isActivitySelected(index: string | number): boolean {
        return this.selectedActivityIdList.some(s => s.index === index);
    }

    public onConceptFilterSelected(concept: ConceptEntity) {
        this.selectedConcepts.push(concept);
        this.setState(this.getNextStepFrom(ActivitySearchEngineStep.CONCEPTS));
        this.lessonSearchOptions = this.getLessonsSearchOptions();
    }

    public onEducationalFilterSelected(educationalLevel: LessonFacetItem) {
        this.selectedEducationalLevels.push(educationalLevel);

        if (this.state === ActivitySearchEngineStep.EDUCATIONAL_LEVELS_FROM_EXCLUSIVE_LIST) {
            this.selectedEducationalLevels.push(educationalLevel);
            const filters: ChapterFilters = {
                grade: [educationalLevel.id],
                concepts: this.selectedConcepts.map(concept => concept.id)
            };
            this.chaptersService.getChapters({filters}).subscribe(chapters => {
                if (chapters.length > 0) {
                    this.state = ActivitySearchEngineStep.CHAPTERS;
                } else {
                    this.state = ActivitySearchEngineStep.LESSONS;
                }
                this.lessonSearchOptions = this.getLessonsSearchOptions();
            });
        } else {
            this.isEducationalLevelsLoading = true;

            const filters = {
                level: educationalLevel.id,
            };

            this.lessonFacetsService.getLessonFacets({filters})
            .subscribe(results => {
                if (results.attributes.chapters.length > 0) {
                    this.state = ActivitySearchEngineStep.CHAPTERS;
                } else {
                    this.state = ActivitySearchEngineStep.LESSONS;
                }
                this.lessonSearchOptions = this.getLessonsSearchOptions();
            });
        }
    }

    public onChapterSelected(chapter: ChapterEntity): void {
        this.selectedChapters.push(chapter);
        this.setState(this.getNextStepFrom(ActivitySearchEngineStep.CHAPTERS));
        this.lessonSearchOptions = this.getLessonsSearchOptions();
    }

    private onActiveEducationalLevelFilterClick($event: MouseEvent, e: LessonFacetItem): void {
        $event.stopPropagation();
        this.selectedEducationalLevels = this.selectedEducationalLevels.filter(c => c.id !== e.id);
        this.isEducationalLevelsLoading = false;
        this.setState(this.getNearestStep(this.getEducationalLevelsStep()));
    }

    private onActiveConceptFilterClick($event: MouseEvent, concept: ConceptEntity): void {
        $event.stopPropagation();
        this.selectedConcepts = this.selectedConcepts.filter(c => c.id !== concept.id);
        this.setState(this.getNearestStep(ActivitySearchEngineStep.CONCEPTS));

    }

    private onActiveChapterFilterClick($event: MouseEvent, c: ChapterEntity): void {
        $event.stopPropagation();
        this.selectedChapters = this.selectedChapters.filter(chapter => chapter.id !== c.id);
        this.setState(this.getNearestStep(ActivitySearchEngineStep.CHAPTERS));
    }

    public onActivityToggleSelectionClick(activityId: string | number, index: string): void {
        if (this.selectedActivityIdList.find(s => s.index === index && s.activity === activityId)) {
            this.selectedActivityIdList = this.selectedActivityIdList.filter(s => s.index !== index && s.activity !== activityId);
            return;
        }
        this.selectedActivityIdList.push({index, activity: activityId});
    }

    public areAllActivitiesSelected(lesson: LessonGranuleEntity, index: string): boolean {
        const activities = lesson.get('reference').map((r, activityIndex) => ({
            activity: r.id,
            index: index + '_' + activityIndex
        }));
        return activities.every(activity => this.selectedActivityIdList.some(selectedActivity => selectedActivity.activity === activity.activity && selectedActivity.index === activity.index));
    }

    public onAllActivitySelectionClick(lesson: LessonGranuleEntity, index: string): void {
        const newActivities = lesson.get('reference')
            .map((r, activityIndex) => ({activity: r.id, index: index + '_' + activityIndex}));

        newActivities.forEach(newActivity => {
            if (!this.selectedActivityIdList.some(existingActivity => existingActivity.activity === newActivity.activity && existingActivity.index === newActivity.index)) {
                this.selectedActivityIdList.push(newActivity);
            }
        });
    }

    public onDeselectAllActivitiesClick(lesson: LessonGranuleEntity, index: string): void {
        const activitiesToDeselect = lesson.get('reference').map((r, activityIndex) =>
            ({activity: r.id, index: index + '_' + activityIndex}));
        this.selectedActivityIdList = this.selectedActivityIdList.filter(selectedActivity =>
            !activitiesToDeselect.some(activityToDeselect =>
                activityToDeselect.activity === selectedActivity.activity && activityToDeselect.index === selectedActivity.index));
    }

    public onDeleteSelectionClick($event: MouseEvent): void {
        $event.stopPropagation();
        this.selectedActivityIdList = [];
    }

    public onImportSelectionClick($event: MouseEvent): void {
        $event.stopPropagation();
        this.selectionValidated.emit(this.selectedActivityIdList.sort((a, b) => a.index.localeCompare(b.index, 'en', {numeric: true})).map(s => s.activity));
    }

    public onPassButtonClick($event: MouseEvent): void {
        $event.stopPropagation();
        this.stateBypassedByUser = this.state;
        this.lessonSearchOptions = this.getLessonsSearchOptions();
        this.setState(ActivitySearchEngineStep.LESSONS);
    }

    public canAddMoreFilters(): boolean {
        return !!this.stateBypassedByUser && this.state === ActivitySearchEngineStep.LESSONS;
    }

    public goBackToFilters(): void {
        this.setState(this.stateBypassedByUser);
        this.stateBypassedByUser = null;
    }

    private normalizeSteps(steps?: ActivitySearchEngineStep[]): ActivitySearchEngineStep[] {
        if (!steps || steps.length === 0) {
            return DEFAULT_ACTIVITY_SEARCH_STEPS.slice();
        }
        const normalized = steps.filter(step => ALLOWED_ACTIVITY_SEARCH_STEPS.includes(step));
        if (normalized.length === 0) {
            return DEFAULT_ACTIVITY_SEARCH_STEPS.slice();
        }
        const uniqueSteps = normalized.filter((step, index) => normalized.indexOf(step) === index);
        const withoutLessons = uniqueSteps.filter(step => step !== ActivitySearchEngineStep.LESSONS);
        return [...withoutLessons, ActivitySearchEngineStep.LESSONS];
    }

    private getFirstStep(): ActivitySearchEngineStep {
        return this.configuredSteps()[0] ?? ActivitySearchEngineStep.LESSONS;
    }

    private getNextStepFrom(step: ActivitySearchEngineStep): ActivitySearchEngineStep {
        const steps = this.configuredSteps();
        const index = steps.indexOf(step);
        if (index === -1) {
            return this.getFirstStep();
        }
        return steps[index + 1] ?? ActivitySearchEngineStep.LESSONS;
    }

    private setState(step: ActivitySearchEngineStep): void {
        if (!step) {
            return;
        }
        const previousState = this.state;
        this.state = step;
        if (previousState !== step) {
            this.onStateEnter(step);
        }
        this.ensureEducationalLevelsLoaded(this.configuredSteps());
    }

    private getNearestStep(step: ActivitySearchEngineStep): ActivitySearchEngineStep {
        const steps = this.configuredSteps();
        return steps.includes(step) ? step : this.getFirstStep();
    }

    private syncSelectionsWithSteps(steps: ActivitySearchEngineStep[]): void {
        if (!steps.includes(ActivitySearchEngineStep.CONCEPTS)) {
            this.selectedConcepts = [];
        }
        if (!this.hasEducationalLevelsStep(steps)) {
            this.selectedEducationalLevels = [];
            this.availableEducationalLevelsToFilters = [];
            this.educationalLevelsLoaded = false;
        }
        if (!steps.includes(ActivitySearchEngineStep.CHAPTERS)) {
            this.selectedChapters = [];
        }
        this.lessonSearchOptions = this.getLessonsSearchOptions();
    }

    private applyConfigSelections(config: ActivitySearchEngineConfig, steps: ActivitySearchEngineStep[]): void {
        if (steps.includes(ActivitySearchEngineStep.EDUCATIONAL_LEVELS_FROM_EXCLUSIVE_LIST) && config?.selectedEducationalLevels?.length) {
            this.selectedEducationalLevels = config.selectedEducationalLevels.slice();
            this.lessonSearchOptions = this.getLessonsSearchOptions();
        }
    }

    private hasEducationalLevelsStep(steps: ActivitySearchEngineStep[]): boolean {
        return steps.includes(ActivitySearchEngineStep.EDUCATIONAL_LEVELS)
            || steps.includes(ActivitySearchEngineStep.EDUCATIONAL_LEVELS_FROM_EXCLUSIVE_LIST);
    }

    private isEducationalLevelsState(step: ActivitySearchEngineStep): boolean {
        return step === ActivitySearchEngineStep.EDUCATIONAL_LEVELS
            || step === ActivitySearchEngineStep.EDUCATIONAL_LEVELS_FROM_EXCLUSIVE_LIST;
    }

    private onStateEnter(step: ActivitySearchEngineStep): void {
        if (this.isEducationalLevelsState(step)) {
            this.isEducationalLevelsLoading = false;
        }
    }

    private getEducationalLevelsStep(): ActivitySearchEngineStep {
        const steps = this.configuredSteps();
        if (steps.includes(ActivitySearchEngineStep.EDUCATIONAL_LEVELS_FROM_EXCLUSIVE_LIST)) {
            return ActivitySearchEngineStep.EDUCATIONAL_LEVELS_FROM_EXCLUSIVE_LIST;
        }
        if (steps.includes(ActivitySearchEngineStep.EDUCATIONAL_LEVELS)) {
            return ActivitySearchEngineStep.EDUCATIONAL_LEVELS;
        }
        return this.getFirstStep();
    }

    private ensureEducationalLevelsLoaded(steps: ActivitySearchEngineStep[]): void {
        if (!steps.includes(ActivitySearchEngineStep.EDUCATIONAL_LEVELS)) {
            return;
        }
        if (this.educationalLevelsLoaded || this.isEducationalLevelsLoading) {
            return;
        }
        this.isEducationalLevelsLoading = true;
        this.educationalLevelService.getEducationalLevels().pipe(take(1)).subscribe((levels) => {
            this.availableEducationalLevelsToFilters = levels.entities.map(level => ({
                label: level.get('label'),
                id: level.id,
                icon: 'add',
                type: 'educationalLevel',
                originalResource: {
                    id: level.id as number,
                    name: level.get('label'),
                    count: '0'
                }
            }));
            this.educationalLevelsLoaded = true;
            this.isEducationalLevelsLoading = false;
        });
    }
}
